import { useState, useCallback, useRef } from 'react';
import { message } from 'antd';
import { pw9208Api, F90M, PW9208CreateRequest } from '../../../services/pw9208Api';
import { PW9208Mode } from './usePW9208Mode';

// Default empty record
const emptyMaster = (): Partial<F90M> => ({
  f90m_01: '',
  f90m_11: '',
  f90m_12: null,
  f90m_13: '',
  f90m_14: '',
  f90m_15: 0,
  f90m_16: 0,
  f90m_17: null,
  f90m_18: 0,
  f90m_19: null,
  f90m_20: '',
  f90m_21: '',
  f90m_22: '',
  f90m_23: null,
  f90m_61: 'Y',
  f90m_62: '',
  f90m_63: null,
  f90m_64: '',
  f90m_66: '',
  f90m_67: '',
  f90m_68: null,
  f90m_69: '',
  f90m_71: '',
  f90m_72: '',
  f90m_73: null,
  f90m_74: '',
  f90m_76: '',
  f90m_77: '',
  f90m_78: null,
  f90m_79: '',
  f90m_80: 'V',
  f90m_91: '',
  f90m_92: null,
  f90m_93: '',
  f90m_151: 0,
  f90m_152: 0,
});

export function usePW9208Record() {
  const [record, setRecord] = useState<F90M | null>(null);
  const [formData, setFormData] = useState<Partial<F90M>>(emptyMaster());
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const originalRef = useRef<Partial<F90M>>(emptyMaster());

  const loadRecord = useCallback(async (id: string): Promise<F90M | null> => {
    setLoading(true);
    try {
      const data = await pw9208Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      originalRef.current = { ...data };
      return data;
    } catch {
      message.error('載入資料失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof F90M, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback(
    <K extends keyof F90M>(key: K): F90M[K] => (formData as F90M)[key],
    [formData]
  );

  const getFieldError = useCallback((key: string) => fieldErrors[key] || '', [fieldErrors]);

  const clearFieldErrors = useCallback(() => setFieldErrors({}), []);

  const resetForAdd = useCallback(() => {
    const empty = emptyMaster();
    setFormData(empty);
    setRecord(null);
    originalRef.current = empty;
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
    }
  }, [record]);

  const applyResult = useCallback((result: F90M) => {
    setRecord(result);
    setFormData({ ...result });
    originalRef.current = { ...result };
  }, []);

  const handleSave = useCallback(
    async (mode: PW9208Mode, onSuccess: (result: F90M) => void) => {
      setLoading(true);
      setFieldErrors({});
      try {
        const payload: PW9208CreateRequest = { master: formData };
        let result: F90M;
        if (mode === 'add') {
          result = await pw9208Api.create(payload);
        } else {
          if (!record?.f90m_01) return;
          result = await pw9208Api.update(record.f90m_01, payload);
        }
        message.success('存檔成功');
        onSuccess(result);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errors?: Array<{ field: string; message: string }> } } };
        if (axiosErr?.response?.data?.errors) {
          const errs: Record<string, string> = {};
          axiosErr.response.data.errors.forEach(e => {
            errs[e.field] = e.message;
          });
          setFieldErrors(errs);
          message.error('資料驗證失敗，請修正後重試');
        } else {
          message.error('存檔失敗');
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, record]
  );

  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record?.f90m_01) return false;
    setLoading(true);
    try {
      await pw9208Api.delete(record.f90m_01);
      message.success('刪除成功');
      setRecord(null);
      setFormData(emptyMaster());
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    } finally {
      setLoading(false);
    }
  }, [record]);

  return {
    record,
    formData,
    loading,
    loadRecord,
    setField,
    getField,
    handleSave,
    handleDelete,
    revertToRecord,
    resetForAdd,
    applyResult,
    getFieldError,
    clearFieldErrors,
  };
}
